<template>
  <div class="modal-mask" v-if="visible" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <h3>{{ wordName }}</h3>
        <div class="header-controls">
          <div class="add-book-wrapper" v-if="showAddButton">
            <button class="add-btn" @click="toggleBookSelect" title="加入单词本">
              <span class="icon">+</span>
            </button>
            <div class="book-dropdown" v-if="showBookSelect">
              <div v-if="loadingBooks" class="dropdown-item loading">加载中...</div>
              <div v-else-if="wordBooks.length === 0" class="dropdown-item empty">暂无单词本</div>
              <div v-else v-for="book in wordBooks" :key="book" class="dropdown-item" @click="addToBook(book)">
                {{ book }}
              </div>
            </div>
          </div>
          <button class="close-btn" @click="close">×</button>
        </div>
      </div>
      
      <div class="modal-body" v-if="loading">
        <div class="loading-spinner">加载中...</div>
      </div>
      
      <div class="modal-body" v-else-if="error">
        <div class="error-message">{{ error }}</div>
      </div>
      
      <div class="modal-body" v-else-if="detail">
        <!-- 音标 -->
        <div class="phonetics-section" v-if="detail.usphone || detail.ukphone">
          <span v-if="detail.ukphone" class="phonetic-item">
            <span class="label">英</span> /{{ detail.ukphone }}/
          </span>
          <span v-if="detail.usphone" class="phonetic-item">
            <span class="label">美</span> /{{ detail.usphone }}/
          </span>
        </div>

        <!-- 释义 -->
        <div class="section" v-if="detail.wordMeaning && (typeof detail.wordMeaning === 'string' || detail.wordMeaning.length)">
          <h4 class="section-title">释义</h4>
          <div class="meaning-list">
            <template v-if="typeof detail.wordMeaning === 'string'">
               <div class="meaning-row">
                 <span class="tran-text">{{ detail.wordMeaning }}</span>
               </div>
            </template>
            <template v-else>
              <div v-for="(item, index) in detail.wordMeaning" :key="index" class="meaning-row">
                <span class="pos-tag">{{ item.pos }}</span>
                <span class="tran-text">{{ item.tran_cn }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- 例句 -->
        <div class="section" v-if="detail.sentences && detail.sentences.length">
          <h4 class="section-title">双语例句</h4>
          <div class="sentence-list">
            <div v-for="(sent, index) in detail.sentences" :key="index" class="sentence-item">
              <p class="en-text" v-html="highlightWord(sent.s_content)"></p>
              <p class="cn-text">{{ sent.s_cn }}</p>
            </div>
          </div>
        </div>

        <!-- 短语 -->
        <div class="section" v-if="detail.phrases && detail.phrases.length">
          <h4 class="section-title">词组短语</h4>
          <div class="phrase-list">
            <div v-for="(phrase, index) in detail.phrases" :key="index" class="phrase-item">
              <span class="phrase-content">{{ phrase.p_content }}</span>
              <span class="phrase-cn">{{ phrase.p_cn }}</span>
            </div>
          </div>
        </div>

        <!-- 同义词 -->
        <div class="section" v-if="detail.synonyms && detail.synonyms.length">
          <h4 class="section-title">同义词</h4>
          <div v-for="(syn, index) in detail.synonyms" :key="index" class="synonym-group">
            <div class="synonym-words">
              <span v-for="(word, wIndex) in syn.Hwds" :key="wIndex" class="synonym-word">
                {{ word.word }}
              </span>
            </div>
            <div class="synonym-header">
              <span class="pos-tag small">{{ syn.pos }}</span>
              <span class="tran-text">{{ syn.tran }}</span>
            </div>
          </div>
        </div>

        <!-- 同根词/相关词 -->
        <div class="section" v-if="detail.relWords && detail.relWords.length">
          <h4 class="section-title">词汇扩展</h4>
          <div v-for="(rel, index) in detail.relWords" :key="index" class="rel-group">
            <div class="pos-label">{{ rel.Pos }}</div>
            <div class="rel-list">
              <div v-for="(hwd, hIndex) in rel.Hwds" :key="hIndex" class="rel-item">
                <span class="rel-word">{{ hwd.hwd }}</span>
                <span class="rel-tran">{{ hwd.tran }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getWordDetail } from '@/api/word'
import { getWordBooks, addWordToBook } from '@/api/wordBook'

export default {
  name: 'WordDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    wordName: {
      type: String,
      required: true
    },
    direction: {
      type: String,
      default: 'en2zh'
    },
    showAddButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      detail: null,
      error: null,
      showBookSelect: false,
      wordBooks: [],
      loadingBooks: false
    }
  },
  watch: {
    visible(val) {
      if (val && this.wordName) {
        this.fetchDetail()
        this.showBookSelect = false
      }
    },
    wordName(val) {
      if (this.visible && val) {
        this.fetchDetail()
        this.showBookSelect = false
      }
    }
  },
  methods: {
    close() {
      this.showBookSelect = false
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    async fetchDetail() {
      this.loading = true
      this.error = null
      this.detail = null
      try {
        const res = await getWordDetail(this.wordName, this.direction)
        if (res.data.success) {
          this.detail = res.data.data
        } else {
          this.error = res.data.errMsg || '获取详情失败'
        }
      } catch (err) {
        this.error = '网络请求失败'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    async toggleBookSelect() {
      if (this.showBookSelect) {
        this.showBookSelect = false
        return
      }
      
      this.showBookSelect = true
      if (this.wordBooks.length === 0) {
        this.loadingBooks = true
        try {
          const userName = localStorage.getItem('userName')
          const res = await getWordBooks(userName)
          if (res.data.success) {
            this.wordBooks = res.data.data
          }
        } catch (e) {
          console.error(e)
          this.$message.error('获取单词本列表失败')
        } finally {
          this.loadingBooks = false
        }
      }
    },
    async addToBook(bookName) {
      try {
        const userName = localStorage.getItem('userName')
        
        // 确保 wordMeaning 转换为字符串
        let wordMeaningStr = "";
        const rawMeaning = this.detail.wordMeaning;
        
        if (rawMeaning) {
          if (typeof rawMeaning === 'string') {
             wordMeaningStr = rawMeaning;
          } else {
             // 无论是数组还是对象，都转换为 JSON 字符串
             wordMeaningStr = JSON.stringify(rawMeaning);
          }
        }
        
        // 构造符合后端要求的参数
        const wordData = {
          wordName: this.detail.wordName,
          wordMeaning: wordMeaningStr,
          ukphone: this.detail.ukphone || "",
          usphone: this.detail.usphone || ""
        }

        const res = await addWordToBook(userName, bookName, wordData)
        if (res.data.success) {
          this.$message.success(`已添加到单词本: ${bookName}`)
          this.showBookSelect = false
        } else {
          this.$message.error(res.data.errMsg || '添加失败')
        }
      } catch (e) {
        console.error('Add to book failed:', e)
        this.$message.error('添加失败')
      }
    },
    highlightWord(text) {
      // 简单的高亮处理，不区分大小写
      if (!text) return ''
      const regex = new RegExp(`(${this.wordName})`, 'gi')
      return text.replace(regex, '<span class="highlight">$1</span>')
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 1500;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-container {
  width: 540px;
  max-width: 94%;
  max-height: 88vh;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
}

.modal-header {
  padding: 20px 24px;
  background: var(--surface);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border);
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-book-wrapper {
  position: relative;
}

.add-btn {
  background: var(--bg-color);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 24px;
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.add-btn:hover {
  background: var(--accent);
  color: #fff;
}

.book-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 160px;
  z-index: 1000;
  overflow-y: auto;
  max-height: 200px;
  border: 1px solid #eee;
  padding: 4px 0;
}

.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s;
  color: #333;
  font-size: 14px;
  text-align: left;
}

.dropdown-item:hover {
  background-color: #f5f7fa;
  color: var(--primary-color);
}

.dropdown-item.loading, .dropdown-item.empty {
  color: #999;
  cursor: default;
  text-align: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.close-btn {
  background: var(--bg-color);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 24px;
  color: var(--text-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.close-btn:hover {
  background: var(--accent);
  color: #fff;
  transform: rotate(90deg);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  color: var(--primary-color);
  font-weight: bold;
}

.error-message {
  text-align: center;
  padding: 40px;
  color: var(--danger-color);
}

.phonetics-section {
  margin-bottom: 24px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.phonetic-item {
  color: var(--text-color);
  font-family: 'Arial', sans-serif;
  background: var(--bg-color);
  padding: 8px 16px;
  border-radius: var(--border-radius-round);
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
}

.phonetic-item .label {
  background: var(--secondary-color);
  padding: 2px 8px;
  border-radius: var(--border-radius-round);
  font-size: 12px;
  color: white;
  margin-right: 8px;
  font-weight: bold;
}

.section {
  margin-bottom: 24px;
  background: var(--surface);
  border-radius: var(--radius);
  padding: 14px 16px;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  color: var(--ink);
  margin: 0 0 12px 0;
  padding-left: 12px;
  border-left: 4px solid var(--accent);
  font-weight: 700;
  display: flex;
  align-items: center;
}

/* 释义样式 */
.meaning-row {
  margin-bottom: 12px;
  line-height: 1.6;
  display: flex;
  align-items: baseline;
}

.pos-tag {
  display: inline-block;
  min-width: 40px;
  color: #fff;
  background: var(--accent);
  font-style: normal;
  margin-right: 12px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  text-align: center;
  box-shadow: 2px 2px 0 rgba(255, 183, 178, 0.4);
}

.pos-tag.small {
  font-size: 12px;
  min-width: auto;
  padding: 1px 6px;
}

.tran-text {
  color: var(--text-color);
  font-weight: 500;
}

/* 例句样式 */
.sentence-item {
  margin-bottom: 16px;
  background: var(--bg-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid rgba(255, 183, 178, 0.2);
  transition: all 0.3s ease;
}

.sentence-item:hover {
  transform: translateX(5px);
  box-shadow: var(--shadow-sm);
  background: white;
}

.sentence-item:last-child {
  margin-bottom: 0;
}

.en-text {
  margin: 0 0 8px 0;
  color: var(--text-color);
  font-size: 16px;
  line-height: 1.5;
  font-weight: 500;
}

.cn-text {
  margin: 0;
  color: #888;
  font-size: 14px;
}

::v-deep .highlight {
  color: var(--accent);
  font-weight: bold;
  background: var(--accent-soft);
  padding: 0 4px;
  border-radius: 4px;
}

/* 短语样式 */
.phrase-item {
  display: flex;
  margin-bottom: 12px;
  align-items: baseline;
  padding-bottom: 8px;
  border-bottom: 1px dashed var(--accent-color);
}

.phrase-item:last-child {
  border-bottom: none;
}

.phrase-content {
  font-weight: 600;
  color: var(--text-color);
  margin-right: 12px;
  min-width: 120px;
}

.phrase-cn {
  color: #666;
}

/* 同义词样式 */
.synonym-group {
  margin-bottom: 16px;
  background: var(--bg-color);
  padding: 12px;
  border-radius: var(--border-radius-md);
}

.synonym-header {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.synonym-words {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.synonym-word {
  background: white;
  color: var(--primary-color);
  padding: 6px 14px;
  border-radius: var(--border-radius-round);
  font-size: 14px;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(255, 183, 178, 0.2);
  transition: all 0.3s;
}

.synonym-word:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
}

/* 同根词样式 */
.rel-group {
  margin-bottom: 16px;
}

.pos-label {
  color: var(--secondary-color);
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: bold;
}

.rel-list {
  padding-left: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.rel-item {
  margin-bottom: 4px;
  background: var(--bg-color);
  padding: 6px 12px;
  border-radius: var(--border-radius-md);
}

.rel-word {
  font-weight: 600;
  color: var(--text-color);
  margin-right: 8px;
}

.rel-tran {
  color: #888;
  font-size: 13px;
}

@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 滚动条美化 */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: var(--accent-color);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-track {
  background-color: transparent;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 16px;
  }
  
  .modal-body {
    padding: 16px;
  }
  
  .phonetic-item {
    font-size: 13px;
    padding: 6px 12px;
  }
  
  .section-title {
    font-size: 16px;
  }
  
  .en-text {
    font-size: 15px;
  }
  
  .phrase-content {
    min-width: 100px;
  }
}
</style>